// The ציר זמן itself — everything the tab used to be, minus the decision of WHERE it is.
//
// Extracted from `TimelineTab.tsx` so the CRM agency-person panel can render the real
// timeline instead of the bespoke mini-feed it used to draw. See the block comment below
// for what that changed and what it deliberately did not.
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  Avatar,
  Button,
  Divider,
  Checkbox,
  MenuItem,
  Skeleton,
  TextField,
  Typography
} from "@material-ui/core";
import {
  CallOutlined,
  FlagOutlined,
  EventOutlined,
  ForumOutlined,
  NotesOutlined,
  FilterListOutlined,
  FormatListBulleted,
  DescriptionOutlined
} from "@material-ui/icons";
import { format } from "date-fns";
import { he } from "date-fns/locale";
import EmptyContent from "components/EmptyContent";
import useAuth from "hooks/useAuth";
import {
  getCached,
  fetchCached,
  subscribeCached,
  useSessionCacheValue
} from "../../data/sessionCache";
import { useContactSurfaceDown } from "../../contactSurface";
import { TabCard } from "../../TabCard";
import { TimelineComposer } from "./TimelineComposer";
import { TimelineItemRow } from "./TimelineItemRow";
import { ContactAgencySections } from "../../../../crm/panel/agency/ContactAgencySections";
import { findLastActivity, mergeAgencyTimeline } from "../../../../crm/panel/agency/agencyPersonModel";
import { createdRowSources } from "./createdRows";
import {
  contactDetailsCacheKey,
  fetchContactDetailsRaw,
  normalizeTimeline,
  timelineCallEnrichmentCacheKey,
  loadTimelineCallEnrichments,
  findCallEnrichment,
  CONTACT_DETAILS_MAX_AGE_MS
} from "./timelineData";
import type { TimelineEntry, TimelineCallEnrichment } from "./timelineData";
import type { REST_schema } from "../../../../../../@types/sharedSchema";
import type { ContactAgencyState } from "../../../../crm/panel/agency/useContactAgency";

/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * ONE TIMELINE, TWO HOSTS
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * `/dashboard/contacts/:id` → ציר זמן tab. Everything the server returned for this person.
 * The CRM agency-person panel      → the same feed, scoped to one agency's holders.
 *
 * The panel used to draw its own list: an avatar, a type word and a timestamp per row. It
 * had none of what makes this one readable — the AI call quotes and recording playback, the
 * rounded call durations, the month separators, the type and team filters, the quick-note
 * composer — and every one of those would have had to be rebuilt there, twice-maintained,
 * to catch up. Asked for as *"instead of having a custom embedded timeline, simply show the
 * full content of the timeline as it is shown in the timeline tab, with the title and
 * everything"*.
 *
 * ── The two differences between the hosts, and why each exists ───────────────────
 *
 * `restrictToAgency` — the panel is TITLED AFTER ONE AGENCY, and the dossier is not scoped
 * to one: `get-details` unions every organization the caller belongs to. Without the
 * intersection the panel could name a colleague from a different agency directly under a
 * list of holders that does not contain them. It is off on the contact page, where the same
 * filter would DELETE rows a realtor has been reading for months. See `mergeAgencyTimeline`.
 *
 * `showAgencyWhenSolo` — the panel IS the agency view, so "only you hold this person" is an
 * answer it must give. On the contact page that block would be furniture above every one of
 * the ~95% of contacts nobody else holds, so it appears only when somebody else does.
 *
 * ── PERMISSIONS ─────────────────────────────────────────────────────────────────
 *
 * Three separate mechanisms, none of them invented here:
 *
 *  1. The holders block renders only what `buildHolderViews` produced for the caller's
 *     `pool`, and the host only asks for holders at all when `browse` is granted. A caller
 *     without it gets a page with no agency block, exactly as before this existed.
 *  2. The FEED is redacted by the server: another org member's free text arrives as the
 *     literal `****`, which `TimelineItemRow` renders as "התוכן מוסתר" rather than as
 *     asterisks. Nothing client-side unblurs it because nothing client-side has it.
 *  3. The call enrichment (AI quote + recording) is read through the שיחות tab's own caches,
 *     which are the caller's own virtual numbers plus — for an org admin — their teammates'.
 *     Same visibility on both tabs by construction.
 */

const PAGE_SIZE = 25;

type TimelineFilterId = "all" | "calls" | "messages" | "meetings" | "documents" | "status" | "notes";

// Client-side activity-type filters (rendered as a dropdown in the filters area). 'status'
// has no backend source yet (no contact audit log) — the option exists per the spec and
// shows an honest empty state until status-change events land in the timeline.
//
// ⚠️ The synthetic `contact-created` rows match NO typed filter, only "הכל" — deliberately.
// They are not an activity of any of these kinds; they are each holder's create time restated
// on the axis. Picking a type is asking for events of that type, and a create row is not one.
const TIMELINE_FILTERS: {
  id: TimelineFilterId;
  label: string;
  Icon: typeof CallOutlined;
  matches: (actionType: string) => boolean;
}[] = [
  { id: "all", label: "הכל", Icon: FormatListBulleted, matches: () => true },
  { id: "calls", label: "שיחות", Icon: CallOutlined, matches: (t) => t === "call" },
  {
    id: "messages",
    label: "הודעות",
    Icon: ForumOutlined,
    matches: (t) => t === "message-sent" || t === "favorite-listings-minisite-creation"
  },
  { id: "meetings", label: "פגישות", Icon: EventOutlined, matches: (t) => t === "meeting" || t === "task" },
  { id: "documents", label: "מסמכים", Icon: DescriptionOutlined, matches: (t) => t === "signature" },
  { id: "status", label: "סטטוס", Icon: FlagOutlined, matches: (t) => t === "status-change" },
  { id: "notes", label: "הערות", Icon: NotesOutlined, matches: (t) => t === "note" }
];

type MonthSection = { label: string; entries: TimelineEntry[] };

function groupByMonth(entries: TimelineEntry[]): MonthSection[] {
  const sections: MonthSection[] = [];
  let currentLabel: string | null = null;
  for (const entry of entries) {
    const label = entry.time ? format(entry.time, "LLLL yyyy", { locale: he }) : "ללא תאריך";
    if (label !== currentLabel) {
      sections.push({ label, entries: [] });
      currentLabel = label;
    }
    sections[sections.length - 1].entries.push(entry);
  }
  return sections;
}

function TimelineSkeleton(): JSX.Element {
  return (
    <Stack spacing={2} sx={{ py: 1 }}>
      {[1, 2, 3].map((i) => (
        <Stack key={i} direction="row" gap={1.5} alignItems="flex-start">
          <Skeleton variant="circular" width={36} height={36} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="35%" height={20} />
            <Skeleton variant="text" width="70%" height={18} />
          </Box>
        </Stack>
      ))}
    </Stack>
  );
}

export type TimelineSurfaceProps = {
  contactId: string;
  /** Who else in the agency holds this person. Resolved by the HOST — see the header. */
  agency: ContactAgencyState;
  /** Drop events from outside this agency's holder set. The panel only. */
  restrictToAgency?: boolean;
  /** Render the agency block even when nobody else holds this person. The panel only. */
  showAgencyWhenSolo?: boolean;
  /** Start the שמור אצל list unfolded. */
  holdersDefaultOpen?: boolean;
  /** Absent where there is no tab strip to jump to — the signature link is then not shown. */
  onOpenSignaturesTab?: () => void;
};

export function TimelineSurface({
  contactId,
  agency,
  restrictToAgency = false,
  showAgencyWhenSolo = false,
  holdersDefaultOpen = false,
  onOpenSignaturesTab
}: TimelineSurfaceProps): JSX.Element {
  /**
   * How wide the SURFACE is, not the window.
   *
   * The filters bar below is the widest fixed thing here: a label, two 190–200px selects and
   * a clear button. A `{ xs: 'column', sm: 'row' }` on it asks the VIEWPORT, so in a CRM side
   * panel — a 420–460px column on a 1440px screen — it answered "row" and laid three
   * desktop-width controls side by side in a box that fits one. Reported as "in the side
   * panel the filters area shows as a desktop view, and there is not enough space for that".
   * Same mistake `contactSurface.tsx` exists to remove; the container decides, not the
   * window. Both panels that render this MUST therefore provide a surface.
   */
  const isNarrow = useContactSurfaceDown("sm");
  const { user } = useAuth();
  const currentUserEmail: string = user?.email ?? "";

  // Shared dossier (same key + fetcher contract as the overview tab, including the SAME
  // maxAge — a shorter age here would re-fire the full get-details dossier the overview just
  // fetched) — renders instantly when the preload already warmed it, silently revalidates
  // when stale.
  const { data, loading, error, refresh } = useSessionCacheValue<
    REST_schema.ContactDetailsResponseSchema
  >(contactDetailsCacheKey(contactId), () => fetchContactDetailsRaw(contactId), {
    maxAgeMs: CONTACT_DETAILS_MAX_AGE_MS
  });

  const serverEntries = useMemo(() => normalizeTimeline(data), [data]);

  const agencyVisible = agency.available && (showAgencyWhenSolo || agency.count > 1);

  /**
   * The server's events plus one "נוצר כרטיס" row per holder, newest first — from the roster
   * where it is drawn, else from the dossier's own copies (`createdRowSources`), so a card nobody
   * else holds still says when it was created.
   *
   * ⚠️ THE SCOPE FLAG IS READ EVEN WHEN THE BLOCK IS NOT SHOWN. `restrictToAgency` is about
   * which events the reader is entitled to see under this title, and `agencyVisible` is
   * about whether a roster is worth drawing — tying the first to the second would mean a
   * panel whose holders failed to load quietly widened its own feed to every agency the
   * caller belongs to.
   */
  const entries = useMemo(() => {
    const sources = createdRowSources({
      agencyVisible,
      holders: agency.holders,
      dossier: data,
      myUserId: currentUserEmail,
      restrictToAgency
    });
    if (!sources.length && !restrictToAgency) return serverEntries;
    return mergeAgencyTimeline(serverEntries, sources, currentUserEmail, {
      restrictToHolders: restrictToAgency
    });
  }, [serverEntries, agencyVisible, agency.holders, data, currentUserEmail, restrictToAgency]);

  // Accessible-calls AI enrichment (summary quote + recording) — same visibility as the
  // שיחות tab (own VNs + org-admin teammates' VNs), derived from the calls tab's shared
  // caches so nothing is fetched twice.
  const [enrichments, setEnrichments] = useState<TimelineCallEnrichment[] | undefined>(() =>
    getCached<TimelineCallEnrichment[]>(timelineCallEnrichmentCacheKey(contactId))
  );
  useEffect(() => {
    if (!contactId) return undefined;
    const key = timelineCallEnrichmentCacheKey(contactId);
    const syncFromCache = (): void => setEnrichments(getCached<TimelineCallEnrichment[]>(key));
    // Sync immediately (covers a contactId change re-running this effect — never leave the
    // previous contact's enrichments on screen).
    syncFromCache();
    const unsubscribe = subscribeCached(key, syncFromCache);
    void fetchCached(key, () => loadTimelineCallEnrichments(contactId), {
      maxAgeMs: 5 * 60_000
    })
      .then(syncFromCache)
      .catch(() => undefined);
    return unsubscribe;
  }, [contactId]);

  const [activeFilter, setActiveFilter] = useState<TimelineFilterId>("all");
  const [hiddenUserIds, setHiddenUserIds] = useState<ReadonlySet<string>>(new Set());
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [contactId, activeFilter, hiddenUserIds]);

  // Org context: when events come from several users, allow toggling users on/off (ported
  // capability from the legacy nested-row timeline filters).
  const distinctUsers = useMemo(() => {
    const byId = new Map<string, TimelineEntry["user"]>();
    entries.forEach((entry) => {
      if (entry.user.userId && !byId.has(entry.user.userId)) byId.set(entry.user.userId, entry.user);
    });
    return Array.from(byId.values());
  }, [entries]);

  // The team dropdown is a multi-select of SHOWN users; hidden = complement.
  const visibleUserIds = useMemo(
    () => distinctUsers.filter((u) => !hiddenUserIds.has(u.userId)).map((u) => u.userId),
    [distinctUsers, hiddenUserIds]
  );
  const handleTeamSelection = (shownIds: string[]): void => {
    const shown = new Set(shownIds);
    setHiddenUserIds(new Set(distinctUsers.filter((u) => !shown.has(u.userId)).map((u) => u.userId)));
  };

  // Per-type counts (after the team filter) — shown inside the type dropdown so the user
  // knows what each option holds before picking it.
  const typeCounts = useMemo(() => {
    const counts = new Map<TimelineFilterId, number>();
    for (const filter of TIMELINE_FILTERS) counts.set(filter.id, 0);
    for (const entry of entries) {
      if (hiddenUserIds.has(entry.user.userId)) continue;
      for (const filter of TIMELINE_FILTERS) {
        if (filter.matches(entry.actionType)) counts.set(filter.id, (counts.get(filter.id) ?? 0) + 1);
      }
    }
    return counts;
  }, [entries, hiddenUserIds]);

  const isFiltered = activeFilter !== "all" || hiddenUserIds.size > 0;
  const clearFilters = (): void => {
    setActiveFilter("all");
    setHiddenUserIds(new Set());
  };

  const filterDef = TIMELINE_FILTERS.find((f) => f.id === activeFilter) ?? TIMELINE_FILTERS[0];
  const filteredEntries = useMemo(
    () =>
      entries.filter(
        (entry) => filterDef.matches(entry.actionType) && !hiddenUserIds.has(entry.user.userId)
      ),
    [entries, filterDef, hiddenUserIds]
  );
  const visibleEntries = useMemo(
    () => filteredEntries.slice(0, visibleCount),
    [filteredEntries, visibleCount]
  );
  const sections = useMemo(() => groupByMonth(visibleEntries), [visibleEntries]);

  const isColdLoading = loading && serverEntries.length === 0;
  const isErrorWithNoData = !!error && !loading && serverEntries.length === 0;

  /**
   * The summary's activity line reads the SAME merged list the rows below come from, so the
   * two can never disagree about what happened last.
   *
   * `Date.now()` at read time rather than a memo dependency: "is this in the future" is a
   * question about NOW, and a memoized answer would go stale exactly when a meeting
   * scheduled for later today becomes a meeting that has happened.
   */
  const lastActivity = agencyVisible ? findLastActivity(entries, Date.now()) : null;

  return (
    <TabCard sx={{ maxWidth: 880, mx: "auto" }}>
      <Stack direction="row" alignItems="baseline" gap={1} sx={{ mb: 2 }}>
        <Typography variant="h6">ציר זמן מלא</Typography>
        {!isColdLoading && (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {filteredEntries.length} אירועים
          </Typography>
        )}
      </Stack>

      {agencyVisible && (
        <ContactAgencySections
          agency={agency}
          lastActivity={lastActivity}
          activityLoading={isColdLoading}
          defaultHoldersOpen={holdersDefaultOpen}
        />
      )}

      <TimelineComposer contactId={contactId} />

      {/* Filters area — a dedicated bar: activity-type dropdown (with per-type counts), a
          team multi-select in org context, and a one-click clear. */}
      <Stack
        direction={isNarrow ? "column" : "row"}
        alignItems={isNarrow ? "stretch" : "center"}
        gap={1.25}
        sx={{
          mb: 2,
          px: 1.5,
          py: 1.25,
          borderRadius: 1.5,
          bgcolor: "background.neutral",
          border: (theme) => `1px solid ${theme.palette.divider}`
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          gap={0.75}
          sx={{ color: "text.secondary", flexShrink: 0 }}
        >
          <FilterListOutlined fontSize="small" />
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            סינון
          </Typography>
        </Stack>

        <TextField
          select
          size="small"
          label="סוג פעילות"
          value={activeFilter}
          onChange={(e) => setActiveFilter(e.target.value as TimelineFilterId)}
          sx={{
            minWidth: isNarrow ? undefined : 200,
            "& .MuiOutlinedInput-root": { bgcolor: "background.paper" }
          }}
          SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 380 } } } }}
        >
          {TIMELINE_FILTERS.map((filter) => {
            const FilterIcon = filter.Icon;
            const count = typeCounts.get(filter.id) ?? 0;
            return (
              <MenuItem key={filter.id} value={filter.id} sx={{ py: 0.75 }}>
                <Stack direction="row" alignItems="center" gap={1} sx={{ width: "100%" }}>
                  <FilterIcon sx={{ fontSize: 17, color: "text.secondary" }} />
                  <Typography component="span" variant="body2" sx={{ flex: 1 }}>
                    {filter.label}
                  </Typography>
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ color: count ? "text.secondary" : "text.disabled", fontWeight: 700 }}
                  >
                    {count}
                  </Typography>
                </Stack>
              </MenuItem>
            );
          })}
        </TextField>

        {distinctUsers.length > 1 && (
          <TextField
            select
            size="small"
            label="אנשי צוות"
            value={visibleUserIds}
            onChange={(e) => handleTeamSelection(e.target.value as unknown as string[])}
            sx={{
              minWidth: isNarrow ? undefined : 190,
              "& .MuiOutlinedInput-root": { bgcolor: "background.paper" }
            }}
            SelectProps={{
              multiple: true,
              renderValue: (selected) => {
                const ids = selected as string[];
                return ids.length === distinctUsers.length
                  ? "כל הצוות"
                  : `${ids.length} מתוך ${distinctUsers.length}`;
              },
              MenuProps: { PaperProps: { sx: { maxHeight: 380 } } }
            }}
          >
            {distinctUsers.map((timelineUser) => (
              <MenuItem key={timelineUser.userId} value={timelineUser.userId} sx={{ py: 0.5 }}>
                <Checkbox
                  size="small"
                  checked={!hiddenUserIds.has(timelineUser.userId)}
                  sx={{ p: 0.5, marginInlineEnd: 0.5 }}
                />
                <Avatar
                  src={timelineUser.userAvatar}
                  sx={{ width: 24, height: 24, marginInlineEnd: 1 }}
                >
                  {(timelineUser.userName || "?").charAt(0).toUpperCase()}
                </Avatar>
                <Typography variant="body2" noWrap>
                  {timelineUser.userName}
                </Typography>
              </MenuItem>
            ))}
          </TextField>
        )}

        {isFiltered && (
          <Button
            size="small"
            color="inherit"
            onClick={clearFilters}
            sx={{
              color: "text.secondary",
              fontWeight: 700,
              flexShrink: 0,
              alignSelf: isNarrow ? "flex-start" : "center"
            }}
          >
            ניקוי סינון
          </Button>
        )}
      </Stack>

      {isColdLoading ? (
        <TimelineSkeleton />
      ) : isErrorWithNoData ? (
        <Stack alignItems="center" spacing={1} sx={{ py: 4 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            טעינת ציר הזמן נכשלה
          </Typography>
          <Button size="small" variant="outlined" onClick={refresh}>
            נסו שוב
          </Button>
        </Stack>
      ) : entries.length === 0 ? (
        <EmptyContent
          title="אין עדיין פעילות"
          description="שיחות, הודעות, פגישות, הערות וחתימות עם הלקוח יופיעו כאן על ציר זמן אחד."
        />
      ) : filteredEntries.length === 0 ? (
        <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", py: 4 }}>
          אין אירועים מסוג זה בציר הזמן
        </Typography>
      ) : (
        <>
          {sections.map((section) => (
            <Box key={section.label}>
              <Stack direction="row" alignItems="center" gap={1.5} sx={{ mt: 2, mb: 0.5 }}>
                <Typography
                  variant="overline"
                  sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
                >
                  {section.label}
                </Typography>
                <Divider sx={{ flex: 1 }} />
              </Stack>
              {section.entries.map((entry) => (
                <TimelineItemRow
                  key={entry.key}
                  entry={entry}
                  enrichment={findCallEnrichment(entry, enrichments)}
                  currentUserEmail={currentUserEmail}
                  onOpenSignaturesTab={onOpenSignaturesTab}
                />
              ))}
            </Box>
          ))}

          {filteredEntries.length > visibleCount && (
            <Stack alignItems="center" sx={{ mt: 2 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
              >
                טען עוד ↓
              </Button>
            </Stack>
          )}
        </>
      )}
    </TabCard>
  );
}
